import {
  useGetStatusQuery,
  useStartBotMutation,
  useStopBotMutation,
  useGetLlmCostSummaryQuery,
  useGetHourlyMetricsQuery,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { PnLPill } from "@/components/domain/PnLPill";
import { Alert } from "@/components/ui/alert";
import { EquityCurve } from "@/components/charts/EquityCurve";
import { DailyPnL } from "@/components/charts/DailyPnL";
import { ProfitMotivation } from "@/components/charts/ProfitMotivation";
import { useGetGridClosedCyclesQuery, useGetProfitTimeseriesQuery, useGetPortfolioBreakdownQuery } from "@/store/api";
import { fmtUsd } from "@/lib/format";
import { Play, Square } from "lucide-react";
import { toast } from "sonner";
import { useAppSelector } from "@/store";
import UserOverview from "@/routes/UserOverview";
import { SpotInfraCard } from "@/components/domain/SpotInfraCard";
import { HourlyOpenPositions } from "@/components/domain/HourlyOpenPositions";
import { DcaHoldings } from "@/components/domain/DcaHoldings";
import { PaxgRebalanceCard } from "@/components/domain/PaxgRebalanceCard";
import { GovernorCard } from "@/components/domain/GovernorCard";
import { ContrarianSignalCard } from "@/components/domain/ContrarianSignalCard";
import { LiquidationHeatmapCard } from "@/components/domain/LiquidationHeatmapCard";
import { DipDcaCard } from "@/components/domain/DipDcaCard";
import { InventorySwingCard } from "@/components/domain/InventorySwingCard";
import { BtcDominancePill } from "@/components/domain/BtcDominancePill";
import { MarketRegimePill } from "@/components/domain/MarketRegimePill";
import { SystemPictureCard } from "@/components/domain/SystemPictureCard";
import { DailyPnLCard } from "@/components/domain/DailyPnLCard";
import { EngineHealthStrip } from "@/components/domain/EngineHealthStrip";

export default function Overview() {
  const role = useAppSelector((s) => s.auth.user?.role);
  if (role !== "admin") return <UserOverview />;
  return <AdminOverview />;
}

function AdminOverview() {
  const status = useGetStatusQuery(undefined, { pollingInterval: 10000 });
  const llmCost = useGetLlmCostSummaryQuery(1, { pollingInterval: 60000 });
  const hourlyMetrics = useGetHourlyMetricsQuery(50, { pollingInterval: 60000 });
  // Combined ROI curve: admin's closed hourly + scalp closes, merged into
  // a single cumulative equity line. /performance has the deeper per-bot
  // breakdown if you want it.
  const gridCycles = useGetGridClosedCyclesQuery(500, { pollingInterval: 60000 });
  const profitTs = useGetProfitTimeseriesQuery(30, { pollingInterval: 60000 });
  // (Closed hourly/scalp data no longer fetched here — Overview now shows
  // grid + total-profit charts instead. /performance has the per-bot deep dive.)
  const [startBot, { isLoading: starting }] = useStartBotMutation();
  const [stopBot, { isLoading: stopping }] = useStopBotMutation();

  const s = status.data;
  // Correct portfolio totals from the breakdown endpoint (free+locked,
  // ALL assets incl. non-whitelist). /api/status only reports free
  // holdings of whitelisted symbols, so it badly undercounts the total.
  const breakdown = useGetPortfolioBreakdownQuery(undefined, { pollingInterval: 60000 });
  const bk = breakdown.data?.summary;

  const settings = (s?.settings ?? {}) as Record<string, unknown>;
  const totalPositionValue = bk?.holdings_usd ?? (s?.position_value_usd ?? 0);
  const freeQuote = bk?.usdt_free ?? (s?.free_quote ?? 0);
  const tradesToday = s?.trades_today ?? 0;
  const sDryRun = settings.dry_run as boolean | undefined;
  const sApproval = settings.approval_mode as "manual" | "auto" | undefined;
  const sPaused = settings.paused as boolean | undefined;

  const onStartBot = async () => {
    try { await startBot().unwrap(); toast.success("Bot started"); }
    catch { toast.error("Start failed"); }
  };
  const onStopBot = async () => {
    try { await stopBot().unwrap(); toast.warning("Bot paused"); }
    catch { toast.error("Stop failed"); }
  };

  return (
    <div className="space-y-4">
      {/* Regime context — BTC.D + market-regime pill sit at the very
          top so it's the first thing visible. Drives prompt + scalp
          gate behavior + the dynamic-overlay adaptive rules. */}
      <div className="flex flex-wrap gap-2">
        <BtcDominancePill />
        <MarketRegimePill />
      </div>

      {/* KPI row — headline numbers first. */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <KpiCard label="Free quote" loading={status.isLoading}>
          <span className="tabular text-2xl font-semibold">{fmtUsd(freeQuote)}</span>
        </KpiCard>
        <KpiCard label="Position value" loading={status.isLoading}>
          <span className="tabular text-2xl font-semibold">{fmtUsd(totalPositionValue)}</span>
        </KpiCard>
        <KpiCard label="Trades today" loading={status.isLoading}>
          <span className="tabular text-2xl font-semibold">{tradesToday}</span>
        </KpiCard>
      </div>

      {/* Total portfolio — moved to the top with the KPIs so the money
          summary is never buried below engine cards. */}
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>Total portfolio</CardTitle>
          <div className="flex gap-2">
            {sPaused ? (
              <Button variant="primary" size="sm" disabled={starting} onClick={onStartBot}>
                <Play className="w-3.5 h-3.5" /> Start bot
              </Button>
            ) : (
              <Button variant="ghost" size="sm" disabled={stopping} onClick={onStopBot}>
                <Square className="w-3.5 h-3.5" /> Stop bot
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge tone={sPaused ? "warn" : "good"}>
              {sPaused ? "paused" : "running"}
            </Badge>
            <Badge tone={sDryRun ? "warn" : "bad"}>
              {sDryRun ? "dry-run" : "live"}
            </Badge>
            <Badge tone={sApproval === "auto" ? "info" : "default"}>
              {sApproval ?? "?"}
            </Badge>
          </div>

          {(() => {
            const positions = (status.data?.open_positions_usd ?? {}) as Record<string, number>;
            const balances = (status.data?.balances ?? {}) as Record<string, number>;
            const freeQuote = Number(status.data?.free_quote ?? 0);
            const positionRows = Object.entries(positions)
              .map(([asset, usd]) => ({
                asset,
                qty: Number(balances[asset] ?? 0),
                usd: Number(usd ?? 0),
              }))
              .filter((r) => r.usd > 0.01)
              .sort((a, b) => b.usd - a.usd);
            const positionsTotal = positionRows.reduce((s, r) => s + r.usd, 0);
            // Use the correct full total (USDT + all holdings, free+locked)
            // from the breakdown; fall back to the status-only sum.
            const grand = bk?.total_portfolio_usd ?? (freeQuote + positionsTotal);
            return (
              <>
                <div className="grid grid-cols-3 gap-3 text-sm pt-1">
                  <div>
                    <div className="text-xs text-muted uppercase tracking-wide">Total</div>
                    <div className="text-lg font-semibold tabular">{fmtUsd(grand)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted uppercase tracking-wide">Free USDT</div>
                    <div className="text-lg font-semibold tabular">{fmtUsd(freeQuote)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted uppercase tracking-wide">Positions</div>
                    <div className="text-lg font-semibold tabular">{fmtUsd(positionsTotal)}</div>
                  </div>
                </div>
                {status.isLoading ? (
                  <Skeleton className="h-32 w-full mt-2" />
                ) : positionRows.length === 0 ? (
                  <p className="text-xs text-muted mt-2">No positions held in whitelisted assets.</p>
                ) : (
                  <Table>
                    <THead>
                      <TR>
                        <TH>Asset</TH>
                        <TH className="text-right">Qty</TH>
                        <TH className="text-right">Value</TH>
                        <TH className="text-right">% of port</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {positionRows.map((r) => (
                        <TR key={r.asset}>
                          <TD className="font-medium">{r.asset}</TD>
                          <TD className="text-right tabular text-xs">{r.qty.toFixed(6)}</TD>
                          <TD className="text-right tabular">{fmtUsd(r.usd)}</TD>
                          <TD className="text-right text-xs text-muted">
                            {grand > 0 ? `${(r.usd / grand * 100).toFixed(1)}%` : "—"}
                          </TD>
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                )}
              </>
            );
          })()}
        </CardContent>
      </Card>

      {/* Today's realized P&L — big number + per-source chips. */}
      <DailyPnLCard />

      {/* Engine health — one dot per engine, breaker states surfaced.
          The alpha 24h loss-cap pill rides in the header. */}
      <EngineHealthStrip />

      {/* Full stack snapshot — portfolio totals, allocation sleeves,
          per-engine allocation + 30d P&L, grid regime. Live on/off
          status lives in the health strip above. */}
      <SystemPictureCard />

      {/* Total profit (motivation) + Grid performance — replaces the old
          cycle/scalp pair charts. Total chart shows cumulative realized
          across ALL engines (the "you ARE making money" view). Grid pair
          shows the workhorse engine's equity + daily breakdown. */}
      <div className="grid lg:grid-cols-2 gap-3">
        <Card>
          <CardHeader>
            <CardTitle>
              💰 Forward earning engines
              <span className="text-xs font-normal text-muted ml-2">
                grid + scalp + inv-swing + hourly · last 30 days · alpha excluded
              </span>
              {profitTs.data && (
                <span className="ml-2">
                  <PnLPill value={Number(profitTs.data.total_realized ?? 0)} />
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {profitTs.isLoading ? (
              <Skeleton className="h-52 w-full" />
            ) : (
              <ProfitMotivation
                days={(profitTs.data?.days ?? []) as any}
                height={200}
              />
            )}
            {profitTs.data?.by_source_total && (
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                {Object.entries(profitTs.data.by_source_total as Record<string, number>)
                  .filter(([, v]) => Math.abs(v) > 0.0001)
                  .sort((a, b) => b[1] - a[1])
                  .map(([src, val]) => (
                    <Badge key={src} tone={val >= 0 ? "good" : "bad"}>
                      {src}: {val >= 0 ? "+" : ""}${val.toFixed(2)}
                    </Badge>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>
              📊 Grid — equity curve
              <span className="text-xs font-normal text-muted ml-2">
                cumulative realized across all 4 grid runs
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {gridCycles.isLoading ? (
              <Skeleton className="h-52 w-full" />
            ) : (
              <EquityCurve positions={(gridCycles.data ?? []) as any} height={200} />
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>
              📊 Grid — daily P&L
              <span className="text-xs font-normal text-muted ml-2">last 14 days</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {gridCycles.isLoading ? (
              <Skeleton className="h-44 w-full" />
            ) : (
              <DailyPnL positions={(gridCycles.data ?? []) as any} days={14} height={180} />
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>
              Grid — recent cycles
              <span className="text-xs font-normal text-muted ml-2">
                {(gridCycles.data ?? []).length} closed cycles in last batch
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {gridCycles.isLoading ? (
              <Skeleton className="h-44 w-full" />
            ) : (
              (() => {
                const cycles = (gridCycles.data ?? []) as any[];
                const bySymbol: Record<string, { n: number; pnl: number }> = {};
                for (const c of cycles) {
                  const s = bySymbol[c.symbol] || { n: 0, pnl: 0 };
                  s.n += 1;
                  s.pnl += Number(c.net_pnl_usd || 0);
                  bySymbol[c.symbol] = s;
                }
                const sorted = Object.entries(bySymbol).sort((a, b) => b[1].pnl - a[1].pnl);
                if (!sorted.length) return <div className="text-muted text-sm">No closed cycles yet.</div>;
                return (
                  <Table>
                    <THead><TR><TH>Symbol</TH><TH className="text-right">Cycles</TH><TH className="text-right">Realized</TH></TR></THead>
                    <TBody>
                      {sorted.map(([sym, s]) => (
                        <TR key={sym}>
                          <TD className="font-mono">{sym}</TD>
                          <TD className="text-right">{s.n}</TD>
                          <TD className="text-right">
                            <span className={s.pnl >= 0 ? "text-good" : "text-bad"}>
                              {s.pnl >= 0 ? "+" : ""}${s.pnl.toFixed(4)}
                            </span>
                          </TD>
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                );
              })()
            )}
          </CardContent>
        </Card>
      </div>

      {/* Engine governor — live regime score + engine state. Tells you
          at a glance why active engines are armed/paused. */}
      <GovernorCard />
      <ContrarianSignalCard />
      <LiquidationHeatmapCard />

      {/* Engine cards */}
      <DipDcaCard />

      {/* Inventory swing — extract chop revenue from held inventory.
          Sells slices near range-high, buys back near range-low.
          Self-tunes daily via Gemini Pro. */}
      <InventorySwingCard />

      {/* DCA cost-basis summary — avg buy, current, unrealized P&L per symbol.
          Hidden automatically when there are no DCA buys. */}
      <DcaHoldings pollMs={30000} />

      {/* PAXG rebalance — admin only, on-demand Gemini consult with explicit
          apply confirmation. Never auto-fires. */}
      <PaxgRebalanceCard />

      {/* Hourly bot — performance + auto-pause banners */}
      {(() => {
        const hm = hourlyMetrics.data;
        if (!hm) return null;
        return (
          <Card>
            <CardHeader>
              <CardTitle>
                Hourly bot — performance
                <span className="text-xs text-muted ml-2 font-normal">
                  rolling window of {hm.window ?? 0} closed positions
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {sPaused && hm.auto_pause_tripped && (
                <Alert tone="danger" title="AUTO-PAUSE active — hourly bot paused">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span>
                      Realized expectancy is negative over the last {hm.window}
                      closed hourly positions. The exit monitor set
                      <code> paused=true</code>.
                    </span>
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={starting}
                      onClick={onStartBot}
                    >
                      <Play className="w-3.5 h-3.5" /> Resume hourly
                    </Button>
                  </div>
                </Alert>
              )}
              {hm.paused && !hm.auto_pause_tripped && (
                <Alert tone="warn" title="Hourly bot paused">
                  No new BUYs will queue until <code>paused</code> is unset in
                  Settings.
                </Alert>
              )}
              {hm.insufficient_data ? (
                <p className="text-sm text-muted">
                  Need at least 5 closed hourly positions for metrics —
                  currently {hm.window ?? 0}.
                </p>
              ) : (
                <div className="grid sm:grid-cols-3 gap-3">
                  <div>
                    <div className="text-xs text-muted uppercase tracking-wide mb-1">Win rate</div>
                    <div className="tabular text-lg font-semibold">
                      {hm.win_rate != null ? `${(hm.win_rate * 100).toFixed(1)}%` : "-"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted uppercase tracking-wide mb-1">Expectancy / trade</div>
                    <div className="text-lg font-semibold"><PnLPill value={hm.expectancy ?? null} /></div>
                  </div>
                  <div>
                    <div className="text-xs text-muted uppercase tracking-wide mb-1">Total P&L</div>
                    <div className="text-lg font-semibold"><PnLPill value={hm.total_pnl ?? null} /></div>
                  </div>
                  <div>
                    <div className="text-xs text-muted uppercase tracking-wide mb-1">Avg win</div>
                    <div className="text-base"><PnLPill value={hm.avg_win ?? null} /></div>
                  </div>
                  <div>
                    <div className="text-xs text-muted uppercase tracking-wide mb-1">Avg loss</div>
                    <div className="text-base"><PnLPill value={hm.avg_loss ?? null} /></div>
                  </div>
                  <div>
                    <div className="text-xs text-muted uppercase tracking-wide mb-1">Consecutive losses</div>
                    <div className="tabular text-base font-semibold">{hm.consecutive_losses ?? 0}</div>
                  </div>
                </div>
              )}
              <p className="text-xs text-muted">
                Auto-pause arms at expectancy &lt; 0 over {hm.auto_pause_window ?? 10} closed
                positions. Toggle in Settings.
              </p>
            </CardContent>
          </Card>
        );
      })()}

      {/* Hourly auto-exit positions — same shared component as the Cycles tab. */}
      <HourlyOpenPositions pollMs={30000} />

      {/* AI cost today */}
      <Card>
        <CardHeader>
          <CardTitle>
            AI cost today
            <span className="text-xs text-muted ml-2 font-normal">
              estimated · resets at midnight local
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {llmCost.isLoading || !llmCost.data ? (
            <Skeleton className="h-16 w-full" />
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-xs text-muted uppercase tracking-wide">Total</div>
                  <div className="text-lg font-semibold tabular">
                    ${(llmCost.data.total_cost_usd || 0).toFixed(4)}
                  </div>
                  <div className="text-xs text-muted">
                    {llmCost.data.total_calls} calls · {(llmCost.data.total_tokens || 0).toLocaleString()} tokens
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs text-muted uppercase tracking-wide mb-1">Per agent</div>
                  <div className="space-y-1">
                    {(llmCost.data.by_agent ?? []).length === 0 && (
                      <div className="text-xs text-muted">No LLM calls today.</div>
                    )}
                    {(llmCost.data.by_agent ?? []).map((a: any) => (
                      <div key={a.agent} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <Badge tone={a.errors > 0 ? "warn" : "muted"}>{a.agent}</Badge>
                          <span className="text-muted">
                            {a.calls} calls · {(a.tokens || 0).toLocaleString()} tokens
                            {a.errors > 0 && <span className="ml-1 text-warn">· {a.errors} err</span>}
                          </span>
                        </div>
                        <div className="tabular font-medium">
                          ${(a.cost_usd || 0).toFixed(4)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted">
                Watchlist refresh now scales 30min → 8h based on idle time;
                narrator skips when the market state hasn't changed. Estimates use Gemini Flash list pricing.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Infrastructure — spot WS streams */}
      <SpotInfraCard />
      {/* PrePumpCard removed — the pre-pump engine is disabled; the deep
          dive stays on /admin/pre-pump if it's ever re-armed. */}
      {/* SimpleEarnCard removed — operator trades, not interested in
          park-for-yield. Keep the module code in case it's wanted later. */}
    </div>
  );
}

function KpiCard({ label, loading, children }: { label: string; loading?: boolean; children: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted uppercase tracking-wide mb-1">{label}</div>
        {loading ? <Skeleton className="h-7 w-24" /> : children}
      </CardContent>
    </Card>
  );
}
